import React, { forwardRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Slide,
  Typography,
  Button,
  Box,
  Paper,
  Stack,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import { Cancel } from "@mui/icons-material";

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const getColorByAction = (action) => {
  const a = String(action).toLowerCase();
  if (a === "list" || a.includes("view") || a === "read") return "info";
  if (a === "create" || a.includes("add")) return "primary";
  if (a === "edit" || a.includes("update")) return "warning";
  if (a === "delete" || a.includes("remove")) return "error";
  return "secondary";
};

const PermissionShowDialog = ({ open, onClose, role }) => {
  const handleClose = () => {
    onClose();
  };

  const permissions = role?.permissions || [];
  const roleName = role?.name || "Role";

  return (
    <Dialog
      open={open}
      keepMounted
      scroll="paper"
      onClose={handleClose}
      TransitionComponent={Transition}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          overflow: "visible",
          width: "600px",
          maxWidth: "95vw",
          backgroundColor: "#1f1f2b",
          color: "#9a9cab",
        },
        translate: "no",
      }}
    >
      <DialogTitle sx={{ pr: 8, color: "#9a9cab" }}>
        <Typography
          variant="h5"
          component="span"
          sx={{ color: "#9a9cab" }}
          translate="yes"
        >
          {roleName} Permissions
        </Typography>
        <Typography
          variant="body1"
          component="div"
          className="mt-2"
          sx={{ color: "#9a9cab" }}
          translate="yes"
        >
          This role has access to {permissions.length} module
          {permissions.length === 1 ? "" : "s"} with various permission levels.
        </Typography>
        <Tooltip title="Close">
          <IconButton
            onClick={handleClose}
            sx={{ position: "absolute", right: 8, top: 8, color: "#e8538f" }}
            size="small"
          >
            <Cancel />
          </IconButton>
        </Tooltip>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          pt: 2,
          color: "#9a9cab",
          borderColor: "#262635",
        }}
      >
        <Stack spacing={2}>
          {permissions.length === 0 ? (
            <Typography sx={{ color: "#9a9cab" }} translate="yes">
              No permissions assigned.
            </Typography>
          ) : (
            permissions.map((permission, index) => (
              <Paper
                key={`${permission.module}-${index}`}
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: "#181821",
                  borderColor: "#262635",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 1,
                    mb: 1.5,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight={600}
                    sx={{ color: "#9a9cab" }}
                    translate="yes"
                  >
                    {permission.module}
                  </Typography>
                  <Chip
                    size="small"
                    label={
                      <span translate="yes">{`${(permission.actions || []).length} action(s)`}</span>
                    }
                    variant="outlined"
                    sx={{
                      color: "#ada6f2",
                      borderColor: "#ada6f2",
                      "& .MuiChip-label": { color: "#ada6f2" },
                    }}
                  />
                </Box>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {(permission.actions || []).map((action, idx) => {
                    const chipColor = getColorByAction(action);
                    const colorMap = {
                      primary: "#86c1ed",
                      info: "#86c1ed",
                      warning: "#f5af47",
                      error: "#e8538f",
                      secondary: "#9a9cab",
                    };
                    const chipColorVal = colorMap[chipColor] || "#9a9cab";
                    return (
                      <Chip
                        key={`${permission.module}-${action}-${idx}`}
                        label={<span translate="yes">{action}</span>}
                        variant="outlined"
                        size="small"
                        sx={{
                          textTransform: "capitalize",
                          color: chipColorVal,
                          borderColor: chipColorVal,
                          "& .MuiChip-label": { color: chipColorVal },
                        }}
                      />
                    );
                  })}
                </Stack>
              </Paper>
            ))
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ pt: 2, borderTop: "1px solid #262635" }}>
        <Button
          onClick={handleClose}
          variant="contained"
          sx={{
            backgroundColor: "#e8538f",
            "&:hover": { backgroundColor: "#d9457a" },
          }}
        >
          <span translate="yes">Close</span>
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PermissionShowDialog;
